import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await register(email, password, fullName);
      toast.success('Welcome to Neural News!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF7EF] flex">
      <div className="hidden lg:flex lg:w-1/2 bg-[#121316] text-white flex-col justify-center px-16">
        <h1 className="news-masthead text-5xl font-bold mb-4">Neural News</h1>
        <p className="text-white/60 text-lg leading-relaxed max-w-md">
          Join our community. Get access to personalized news feeds, comments, and newsletters.
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <Link to="/" className="news-masthead text-2xl font-bold text-[#121316] lg:hidden block mb-8">Neural News</Link>
          <h2 className="text-2xl font-bold mb-1">Create Account</h2>
          <p className="text-[#5B616E] text-sm mb-8">Join Neural News and stay informed</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1.5">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                className="w-full border border-[#E7E0D6] rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#B89B5E] text-sm"
                placeholder="John Doe"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full border border-[#E7E0D6] rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#B89B5E] text-sm"
                placeholder="you@example.com"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full border border-[#E7E0D6] rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#B89B5E] text-sm pr-10"
                  placeholder="Min 6 characters"
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B616E]">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-[#121316] text-white hover:bg-[#1B1D22] py-3 rounded-xl"
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="text-center text-sm text-[#5B616E] mt-6">
            Already have an account? <Link to="/login" className="text-[#1E5A8A] font-medium">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
